import { useEffect, useRef, useState } from "react";
import {
  LuBug,
  LuChevronDown,
  LuClipboardList,
  LuCode,
  LuFileDown,
  LuGift,
  LuImage,
  LuMessageSquare,
  LuSettings,
  LuUpload,
} from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useFeedbackModal } from "../../../providers/FeedbackModalProvider";
import { useFreeTokensModal } from "../../../providers/FreeTokensModalProvider";
import {
  DashboardMode,
  useDashboardControlsActions,
  useWorkspaceSection,
  WorkspaceSection,
} from "../../../view_stores/useDashboardControlsStore";
import { useProjectName } from "../../../view_stores/useProjectConfigStore";
import { useProjectPreviewUrl } from "../../../view_stores/useProjectPreviewStore";

export function ProjectWorkspaceMenu() {
  const isCompactView = useIsCompactView();

  const { show: showFreeTokensModal } = useFreeTokensModal();
  const { setWorkspaceSection, setDashboardMode } =
    useDashboardControlsActions();
  const { open: openFeedbackModal } = useFeedbackModal();

  const sections = [
    // {
    //   id: WorkspaceSection.OVERVIEW,
    //   label: "Overview",
    //   icon: <LuFileChartColumn size={20} />,
    // },
    {
      id: WorkspaceSection.SETTINGS,
      label: "App Settings",
      icon: <LuSettings size={20} />,
    },
    {
      id: WorkspaceSection.ICON,
      label: "App Icon",
      icon: <LuImage size={20} />,
    },
    {
      id: WorkspaceSection.CODE,
      label: "Code",
      icon: <LuCode size={20} />,
    },
    ...(import.meta.env.VITE_PROD === "false"
      ? [
          {
            id: WorkspaceSection.DEBUG,
            label: "Debug",
            icon: <LuBug size={20} />,
          },
        ]
      : []),
    {
      id: WorkspaceSection.PUBLISH,
      label: "Publish",
      icon: <LuUpload size={20} />,
    },
    {
      id: WorkspaceSection.EXPORT,
      label: "Export",
      icon: <LuFileDown size={20} />,
    },
    {
      id: "feedback",
      label: "Feedback",
      icon: <LuMessageSquare size={20} />,
    },
    ...(isCompactView
      ? [
          {
            id: "free-tokens",
            label: "Free Tokens",
            icon: <LuGift size={20} />,
          },
        ]
      : []),
  ];

  function handleSectionClick(section: WorkspaceSection | string) {
    if (section === "feedback") {
      openFeedbackModal();
    } else if (section === "free-tokens") {
      showFreeTokensModal();
    } else {
      setDashboardMode(DashboardMode.WORKSPACE);
      setWorkspaceSection(section as WorkspaceSection);
    }
  }

  return isCompactView ? (
    <CompactMenu sections={sections} handleSectionClick={handleSectionClick} />
  ) : (
    <SideMenu sections={sections} handleSectionClick={handleSectionClick} />
  );
}

function CompactMenu({
  sections,
  handleSectionClick,
}: {
  sections: {
    id: WorkspaceSection | string;
    label: string;
    icon: React.ReactNode;
  }[];
  handleSectionClick: (section: WorkspaceSection | string) => void;
}) {
  const projectName = useProjectName();
  const previewUrl = useProjectPreviewUrl();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSectionClick = (section: WorkspaceSection | string) => {
    setIsDropdownOpen(false);
    handleSectionClick(section);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`${
          isDropdownOpen
            ? "text-light-text dark:text-dark-text bg-white dark:bg-[#272829]"
            : "text-light-text dark:text-dark-text bg-white dark:bg-[#191C1F]"
        } flex items-center gap-1.5 px-3 py-1.5 rounded-md 
        font-medium transition-colors`}
        disabled={!projectName || !previewUrl}
      >
        <LuClipboardList size={18} />
        Workspace
        <LuChevronDown
          size={18}
          className={`transition-transform ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isDropdownOpen && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-48 
        bg-light-surface-primary dark:bg-dark-surface-tertiary
        rounded-md shadow-lg py-1 z-50 
        border-2 border-white dark:border-[#373B40]"
        >
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => onSectionClick(section.id)}
              className="w-full flex items-center gap-3 px-4 py-2 
              text-left text-light-text dark:text-dark-text 
              hover:bg-white dark:hover:bg-[#272829] 
              hover:text-light-text dark:hover:text-dark-text"
            >
              {section.icon}
              <span>{section.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SideMenu({
  sections,
  handleSectionClick,
}: {
  sections: {
    id: WorkspaceSection | string;
    label: string;
    icon: React.ReactNode;
  }[];
  handleSectionClick: (section: WorkspaceSection | string) => void;
}) {
  const [canShowLabels, setCanShowLabels] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const workspaceSection = useWorkspaceSection();

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanShowLabels(entry.contentRect.width > 100);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="h-full w-full p-4">
      <h2
        className="text-light-text dark:text-dark-text text-lg font-semibold 
      mb-6 whitespace-nowrap overflow-hidden text-ellipsis"
      >
        Workspace
      </h2>
      <nav className="space-y-1">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className={`${canShowLabels ? "justify-start" : "justify-center"} ${
              workspaceSection === section.id
                ? "text-light-text dark:text-dark-text bg-white dark:bg-[#272829]"
                : "text-light-text dark:text-dark-text hover:bg-white dark:hover:bg-[#272829]"
            } w-full min-w-[40px] flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors`}
            title={section.label}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              {section.icon}
            </div>
            {canShowLabels && (
              <span className="side-menu-label font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                {section.label}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}
